// client/src/pages/secure/TasksPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';
import PageHeader from '../../components/shared/PageHeader';
import { getCurrentUser, canManageStaff } from '../../hooks/useCurrentUser';

// --- Add Task Modal ---
const AddTaskModal = ({ isOpen, onClose, onTaskAdded }) => {
    const [clients, setClients] = useState([]);
    const [formData, setFormData] = useState({ title: '', description: '', due_date: '', client_id: '' });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();

    useEffect(() => {
        if (isOpen) {
            apiClient.get('/api/clients').then(setClients).catch(() => setClients([]));
            setFormData({ title: '', description: '', due_date: '', client_id: '' });
            setError('');
        }
    }, [isOpen, apiClient]);

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        try {
            const newTask = await apiClient.post('/api/tasks', {
                ...formData,
                client_id: formData.client_id || null,
            });
            onTaskAdded(newTask);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-4 sm:p-8 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto w-full max-w-lg">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Add Task</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input name="title" value={formData.title} onChange={handleChange} placeholder="Task title" required className="p-2 border rounded w-full" />
                    <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Description (optional)" rows="3" className="p-2 border rounded w-full"></textarea>
                    <input type="date" name="due_date" value={formData.due_date} onChange={handleChange} className="p-2 border rounded w-full" />
                    <select name="client_id" value={formData.client_id} onChange={handleChange} className="p-2 border rounded w-full">
                        <option value="">No linked client</option>
                        {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.first_name} {c.last_name}</option>)}
                    </select>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div className="flex justify-end gap-4 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Saving...' : 'Save Task'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const TasksPage = () => {
    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const apiClient = useApiClient();
    const isStaffOrBroker = canManageStaff(getCurrentUser()) || getCurrentUser()?.role === 'admin';

    const fetchTasks = useCallback(() => {
        setIsLoading(true);
        apiClient.get('/api/tasks')
            .then(data => setTasks(data))
            .catch(err => setError(err.message || 'Failed to fetch tasks.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    const handleTaskAdded = (newTask) => {
        setTasks(prev => [newTask, ...prev]);
    };

    const handleToggleComplete = async (task) => {
        try {
            const updated = await apiClient.put(`/api/tasks/${task.task_id}`, {
                status: task.status === 'Completed' ? 'Open' : 'Completed'
            });
            setTasks(prev => prev.map(t => t.task_id === task.task_id ? updated : t));
        } catch (err) {
            alert(err.message || 'Failed to update task.');
        }
    };

    const handleCancel = async (taskId) => {
        if (!window.confirm('Cancel this task?')) return;
        try {
            await apiClient.delete(`/api/tasks/${taskId}`);
            setTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, status: 'Cancelled' } : t));
        } catch (err) {
            alert(err.message || 'Failed to cancel task.');
        }
    };

    const currentUser = getCurrentUser();
    const visibleTasks = tasks
        .filter(t => t.status !== 'Cancelled')
        .filter(t => showAll || !isStaffOrBroker || t.assigned_to_agent_id === currentUser?.agentId);

    return (
        <>
            <AddTaskModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onTaskAdded={handleTaskAdded} />
            <div className="bg-white p-4 sm:p-8 rounded-lg shadow">
                <PageHeader title="Tasks" description="Your to-do list, with due dates and linked clients."
                    actions={
                        <button onClick={() => setIsModalOpen(true)} className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900">
                            Add Task
                        </button>
                    }
                    secondary={isStaffOrBroker ? (
                        <label className="flex items-center gap-2 text-sm text-slate-600">
                            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                            Show all tasks
                        </label>
                    ) : null} />

                {isLoading && <p>Loading tasks...</p>}
                {error && <p className="text-red-500">{error}</p>}
                {!isLoading && !error && (
                    <DataList rows={visibleTasks} rowKey="task_id" csvName="tasks" defaultSort={{ key: 'due_date', dir: 'asc' }}
                        filters={[{ key: 'status', label: 'Status' }, { key: 'assigned_to_name', label: 'Assigned to' }]}
                        empty="No tasks found."
                        columns={[
                            { key: 'title', label: 'Task', value: (t) => `${t.title}${t.description ? ` — ${t.description}` : ''}`, render: (task) => (<div>
                                <p className={`font-medium ${task.status === 'Completed' ? 'line-through text-slate-400' : 'text-slate-800'}`}>{task.title}</p>
                                {task.description && <p className="text-xs text-slate-500 mt-1">{task.description}</p>}
                            </div>) },
                            { key: 'due_date', label: 'Due', type: 'date' },
                            { key: 'client_name', label: 'Client' },
                            { key: 'assigned_to_name', label: 'Assigned to' },
                            { key: 'status', label: 'Status' },
                            { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (task) => (
                                <div className="flex items-center gap-3 whitespace-nowrap">
                                    <button onClick={() => handleToggleComplete(task)} className="text-sm text-indigo-600 hover:underline">{task.status === 'Completed' ? 'Reopen' : 'Mark Complete'}</button>
                                    <button onClick={() => handleCancel(task.task_id)} className="text-sm text-red-500 hover:underline">Cancel</button>
                                </div>) },
                        ]} />
                )}
            </div>
        </>
    );
};

export default TasksPage;
